<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Articles</h4>
      <div class="d-flex align-items-center">
        <el-input
          v-model="searchQuery"
          clearable
          placeholder="Search articles..."
          style="width: 300px"
          class="mr-3"
        />
        <el-select v-model="filterLang" placeholder="Language" clearable style="width: 120px" class="mr-3">
          <el-option v-for="l in langs" :key="l" :label="l.toUpperCase()" :value="l" />
        </el-select>
        <el-select v-model="filterHide" placeholder="Hidden" clearable style="width: 120px" class="mr-3">
          <el-option label="No" :value="false" />
          <el-option label="Yes" :value="true" />
        </el-select>
        <el-button type="primary" class="mr-2" @click="applyFilters">Filter</el-button>
        <el-button class="mr-3" @click="clearFilters">Clear filter</el-button>
        <router-link :to="{ name: 'articles-create' }" class="btn btn-primary create-btn">Create</router-link>
      </div>
    </div>

    <card>
      <el-table :data="articlesList" v-loading="loading" :row-class-name="rowClass" style="width: 100%">
        <el-table-column label="Name" prop="name" min-width="300" />
        <el-table-column label="Author" width="180">
          <template slot-scope="{ row }">
            <span v-if="row.author">{{ row.author.first_name }} {{ row.author.last_name }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Editor" width="180">
          <template slot-scope="{ row }">
            <span v-if="row.editor">{{ row.editor.first_name }} {{ row.editor.last_name }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Hidden" width="80">
          <template slot-scope="{ row }">
            {{ row.hide ? 'Yes' : 'No' }}
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="Edit" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="editArticle(row.id)">Edit</el-button>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="Delete" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" type="danger" @click="handleDelete(row)">Del</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="text-center mt-3" v-if="articlesTotal > perPage">
        <el-pagination
          :page-size="perPage"
          :total="articlesTotal"
          :current-page="currentPage"
          layout="prev, pager, next"
          @current-change="changePage"
        />
      </div>
    </card>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

const PER_PAGE = 100;

export default {
  data() {
    return {
      loading: false,
      currentPage: 1,
      perPage: PER_PAGE,
      filterLang: "",
      filterHide: null,
      searchQuery: "",
    };
  },
  computed: {
    ...mapState("articles", ["articlesList", "articlesTotal"]),
    ...mapState("cmsApiCatalogs", ["domains"]),
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
  },
  methods: {
    ...mapActions("articles", ["getArticles", "deleteArticle"]),
    ...mapActions("cmsApiCatalogs", ["getDomains"]),
    setPage(page) {
      this.currentPage = Math.max(1, page);
      this.$router.replace({ query: { ...this.$route.query, page: this.currentPage } }).catch(() => {});
    },
    async loadList() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, "per-page": this.perPage };
        if (this.filterLang) params.lang = this.filterLang;
        if (this.searchQuery) params.search = this.searchQuery;
        // Omit when unset — empty ?hide= is treated as false by cms-api.
        if (this.filterHide !== null && this.filterHide !== undefined) {
          params.hide = this.filterHide;
        }
        await this.getArticles(params);
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      this.setPage(page);
      this.loadList();
    },
    applyFilters() {
      this.setPage(1);
      this.loadList();
    },
    clearFilters() {
      this.filterLang = "";
      this.filterHide = null;
      this.searchQuery = "";
      this.setPage(1);
      this.loadList();
    },
    editArticle(id) {
      this.$router.push({ name: "articles-edit", params: { id } });
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`Delete article "${row.name || row.id}"?`, "Warning", {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        });
        const [lang, ...aliasParts] = row.id.split(":");
        this.loading = true;
        await this.deleteArticle({ lang, alias: aliasParts.join(":") });
        this.$notify({ type: "success", title: "Article deleted" });
        await this.loadList();
      } catch (e) {
        if (e !== "cancel") {
          this.$notify({ type: "warning", title: "Error deleting article" });
        }
      } finally {
        this.loading = false;
      }
    },
    rowClass({ row }) {
      return row.hide ? "warning-row" : "";
    },
  },
  async created() {
    const page = parseInt(this.$route.query.page, 10);
    if (page > 0) this.currentPage = page;
    await this.getDomains();
    this.loadList();
  },
};
</script>

<style scoped>
.el-table >>> .warning-row {
  background-color: #fcf8e3;
}

.el-select {
  margin-bottom: 0;
}

.create-btn {
  height: 40px;
  display: inline-flex;
  align-items: center;
}
</style>
